import { useState, useEffect } from "react";
import { collection, query, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { ArrowLeft, Video, ImageIcon, File, Trash2 } from "lucide-react";
import { MediaType } from "../types/media";
import { deleteFromR2 } from "../services/r2Service";
import MediaUpload from "./MediaUpload";
import OptimizedVideoPlayer from "./OptimizedVideoPlayer";
import FileDownload from "./FileDownload";

interface MediaManagerProps {
  courseId: string;
  mediaType: MediaType;
}

export default function MediaManager({ courseId, mediaType }: MediaManagerProps) {
  const [mediaList, setMediaList] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);

  useEffect(() => {
    loadMedia();
  }, [courseId, mediaType]);

  const loadMedia = async () => {
    try {
      const mediaRef = collection(db, "courses", courseId, "media");
      const q = query(mediaRef, where("type", "==", mediaType));
      const snapshot = await getDocs(q);

      const media: Media[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Media[];

      media.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setMediaList(media);
      setLoading(false);
    } catch (error) {
      console.error("Error loading media:", error);
      setLoading(false);
    }
  };

  const handleDelete = async (mediaId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ที่จะลบไฟล์นี้?")) return;

    try {
      // Get the media document to extract the R2 key
      const mediaToDelete = mediaList.find(m => m.id === mediaId);
      
      // Delete from Firestore first
      await deleteDoc(doc(db, "courses", courseId, "media", mediaId));
      
      // If media has a URL, try to extract and delete from R2
      if (mediaToDelete?.url) {
        try {
          // Extract key from URL (format: https://domain/key)
          const url = new URL(mediaToDelete.url);
          const key = url.pathname.substring(1); // Remove leading slash
          
          // Only attempt delete if it's an R2 URL (contains uploads/)
          if (key.includes('uploads/')) {
            await deleteFromR2(key);
            console.log('✅ File deleted from R2:', key);
          }
        } catch (r2Error) {
          console.warn('Could not delete from R2:', r2Error);
          // Don't fail the whole operation if R2 delete fails
        }
      }
      
      setMediaList(mediaList.filter((m) => m.id !== mediaId));
      if (selectedMedia?.id === mediaId) {
        setSelectedMedia(null);
      }
    } catch (error) {
      console.error("Error deleting media:", error);
      alert("เกิดข้อผิดพลาดในการลบไฟล์");
    }
  };

  const getMediaIcon = () => {
    switch (mediaType) {
      case "video":
        return <Video className="w-6 h-6" />;
      case "image":
        return <ImageIcon className="w-6 h-6" />;
      case "file":
        return <File className="w-6 h-6" />;
    }
  };

  const getMediaLabel = () => {
    switch (mediaType) {
      case "video":
        return "วิดีโอ";
      case "image":
        return "รูปภาพ";
      case "file":
        return "ไฟล์ประกอบการสอน";
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="text-blue-600">{getMediaIcon()}</div>
        <h2 className="text-2xl font-bold text-gray-800">
          จัดการ{getMediaLabel()}
        </h2>
      </div>

      {/* Upload Section */}
      <MediaUpload
        courseId={courseId}
        mediaType={mediaType}
        onUploadComplete={() => loadMedia()}
        onUploadError={(error) => alert(error)}
      />

      {/* Media List */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h3 className="text-xl font-bold text-gray-800 mb-4">
          {getMediaLabel()}ที่อัปโหลดแล้ว ({mediaList.length})
        </h3>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">กำลังโหลด...</p>
          </div>
        ) : mediaList.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-300 mb-4">{getMediaIcon()}</div>
            <p className="text-gray-600">ยังไม่มี{getMediaLabel()}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mediaList.map((media) => (
              <div
                key={media.id}
                className="bg-gray-50 rounded-xl p-4 hover:shadow-md transition-all"
              >
                {/* Thumbnail/Preview */}
                {mediaType === "image" && (
                  <img
                    src={media.url}
                    alt={media.fileName}
                    className="w-full h-40 object-cover rounded-lg mb-3"
                    loading="lazy"
                  />
                )}
                {mediaType === "video" && (
                  <div className="relative w-full h-40 bg-black rounded-lg mb-3 overflow-hidden">
                    <video
                      src={media.url}
                      className="w-full h-full object-cover"
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Video className="w-12 h-12 text-white" />
                    </div>
                  </div>
                )}
                {mediaType === "file" && (
                  <div className="w-full h-40 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg mb-3 flex items-center justify-center">
                    <File className="w-16 h-16 text-blue-600" />
                  </div>
                )}

                {/* Info */}
                <div className="mb-3">
                  <h4 className="font-semibold text-gray-800 truncate mb-1">
                    {media.fileName}
                  </h4>
                  {media.fileSize && (
                    <p className="text-sm text-gray-600">
                      {formatFileSize(media.fileSize)}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedMedia(media)}
                    className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    ดู
                  </button>
                  <button
                    onClick={() => handleDelete(media.id)}
                    className="bg-red-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-red-700 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                {selectedMedia.fileName}
              </h3>
              <button
                onClick={() => setSelectedMedia(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            {mediaType === "video" && (
              <OptimizedVideoPlayer 
                originalUrl={selectedMedia.url} 
                jobId={selectedMedia.jobId}
                title={selectedMedia.fileName} 
              />
            )}

            {mediaType === "image" && (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.fileName}
                className="w-full rounded-lg"
              />
            )}

            {mediaType === "file" && (
              <FileDownload
                url={selectedMedia.url}
                fileName={selectedMedia.fileName}
                fileSize={selectedMedia.fileSize}
                contentType={selectedMedia.contentType}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
